





























































































































































import { Component } from "vue-property-decorator";

import { SchedulingSettings, WorkingHoursSettings } from "@/data/models/SchedulingSettings";

import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import TimePickerComponent from "@/components/common/TimePicker.vue";
import ColorPickerDialogComponent from "@/components/common/ColorPickerDialog.vue";
import ConfirmationDialog from "@/components/common/ConfirmationDialog.vue";
import { DateTime } from "luxon";
import { firestore } from "firebase";
import { SchedulingModule } from "@/store/modules/SchedulingModule";
import { loadSchedulingSettings, saveSchedulingSettings } from "@/data/repository/SchedulingSettingsRepository";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";

@Component({
  components: {
    "time-picker": TimePickerComponent,
    "color-picker": ColorPickerDialogComponent,
    "confirmation-dialog": ConfirmationDialog,
    picker: PickerDialogComponent,
  },
})
export default class WorkingHoursSettingsComponent extends SettingsPageMixin {
  schedulingSettings = { ...SchedulingModule.schedulingSettings };
  private initialSchedulingSettings: SchedulingSettings | null = null;

  get defaultWorkingHoursPerDay() {
    return this.workingHoursSettings.defaultWorkingHoursPerDay;
  }

  set defaultWorkingHoursPerDay(value: number) {
    this.updateWorkingHours(value, "defaultWorkingHoursPerDay");
  }

  get maxWorkingHoursPerDay() {
    return this.workingHoursSettings.maxWorkingHoursPerDay;
  }

  set maxWorkingHoursPerDay(value: number) {
    this.updateWorkingHours(value, "maxWorkingHoursPerDay");
  }

  get defaultWorkingHoursPerWeek() {
    return this.workingHoursSettings.defaultWorkingHoursPerWeek;
  }

  set defaultWorkingHoursPerWeek(value: number) {
    this.updateWorkingHours(value, "defaultWorkingHoursPerWeek");
  }

  get maxWorkingHoursPerWeek() {
    return this.workingHoursSettings.maxWorkingHoursPerWeek;
  }

  set maxWorkingHoursPerWeek(value: number) {
    this.updateWorkingHours(value, "maxWorkingHoursPerWeek");
  }

  get lunchBreakDurationInMin() {
    return this.workingHoursSettings.lunchBreakDurationInMin;
  }

  set lunchBreakDurationInMin(value: number) {
    this.updateWorkingHours(value, "lunchBreakDurationInMin");
  }

  get shortBreakThresholdHours() {
    return this.workingHoursSettings.shortBreakThresholdHours ?? 6;
  }

  set shortBreakThresholdHours(value: number) {
    this.updateWorkingHours(value, "shortBreakThresholdHours");
  }

  get longBreakThresholdHours() {
    return this.workingHoursSettings.longBreakThresholdHours ?? 9;
  }

  set longBreakThresholdHours(value: number) {
    this.updateWorkingHours(value, "longBreakThresholdHours");
  }

  get longBreakDurationInMin() {
    return this.workingHoursSettings.longBreakDurationInMin ?? 45;
  }

  set longBreakDurationInMin(value: number) {
    this.updateWorkingHours(value, "longBreakDurationInMin");
  }

  get minBreakDurationInMin() {
    return this.workingHoursSettings.minBreakDurationInMin ?? 15;
  }

  set minBreakDurationInMin(value: number) {
    this.updateWorkingHours(value, "minBreakDurationInMin");
  }

  get defaultWorkDayStart() {
    return this.getTimestampForHourAndMinutes("defaultWorkDayStart");
  }

  set defaultWorkDayStart(timestamp: firestore.Timestamp) {
    this.updateHoursAndMinutes(timestamp, "defaultWorkDayStart");
  }

  get defaultWorkDayEnd() {
    return this.getTimestampForHourAndMinutes("defaultWorkDayEnd");
  }

  set defaultWorkDayEnd(timestamp: firestore.Timestamp) {
    this.updateHoursAndMinutes(timestamp, "defaultWorkDayEnd");
  }

  get earliestWorkDayStart() {
    return this.getTimestampForHourAndMinutes("earliestWorkDayStart");
  }

  set earliestWorkDayStart(timestamp: firestore.Timestamp) {
    this.updateHoursAndMinutes(timestamp, "earliestWorkDayStart");
  }

  get latestWorkDayEnd() {
    return this.getTimestampForHourAndMinutes("latestWorkDayEnd");
  }

  set latestWorkDayEnd(timestamp: firestore.Timestamp) {
    this.updateHoursAndMinutes(timestamp, "latestWorkDayEnd");
  }

  get lunchBreakAfter() {
    return this.getTimestampForHourAndMinutes("lunchBreakAfter");
  }

  set lunchBreakAfter(timestamp: firestore.Timestamp) {
    this.updateHoursAndMinutes(timestamp, "lunchBreakAfter");
  }

  get workingHoursSettings() {
    return this.schedulingSettings.workingHoursSettings;
  }

  set workingHoursSettings(workingHoursSettings: WorkingHoursSettings) {
    this.schedulingSettings = {
      ...this.schedulingSettings,
      workingHoursSettings: workingHoursSettings,
    };
  }

  updateWorkingHours(
    newWorkingHours: number,
    fieldName:
      | "defaultWorkingHoursPerDay"
      | "maxWorkingHoursPerDay"
      | "defaultWorkingHoursPerWeek"
      | "maxWorkingHoursPerWeek"
      | "lunchBreakDurationInMin"
      | "shortBreakThresholdHours"
      | "longBreakThresholdHours"
      | "longBreakDurationInMin"
      | "minBreakDurationInMin"
  ) {
    this.updateWorkingHourSettings((workingHoursSettings) => {
      workingHoursSettings[fieldName] = newWorkingHours;
    });
  }

  updateHoursAndMinutes(
    timestamp: firestore.Timestamp,
    fieldName:
      | "defaultWorkDayStart"
      | "defaultWorkDayEnd"
      | "earliestWorkDayStart"
      | "latestWorkDayEnd"
      | "lunchBreakAfter"
  ) {
    this.updateWorkingHourSettings((workingHoursSettings) => {
      workingHoursSettings[fieldName] = this.getHoursAndMinutesFromTimestamp(
        timestamp
      );
    });
  }

  getTimestampForHourAndMinutes(
    fieldName:
      | "defaultWorkDayStart"
      | "defaultWorkDayEnd"
      | "earliestWorkDayStart"
      | "latestWorkDayEnd"
      | "lunchBreakAfter"
  ) {
    const { hour, minute } = this.workingHoursSettings[fieldName];

    return firestore.Timestamp.fromDate(
      DateTime.fromJSDate(new Date())
        .set({
          hour: hour,
          minute: minute,
          second: 0,
          millisecond: 0,
        })
        .toJSDate()
    );
  }

  getHoursAndMinutesFromTimestamp(timetamp: firestore.Timestamp) {
    const dateTime = DateTime.fromJSDate(timetamp.toDate());

    return {
      hour: dateTime.hour,
      minute: dateTime.minute,
    };
  }

  updateWorkingHourSettings(
    callback: (workingHourSettings: WorkingHoursSettings) => void
  ) {
    const currentValue = this.workingHoursSettings;
    callback(currentValue);

    this.schedulingSettings = {
      ...this.schedulingSettings,
      workingHoursSettings: currentValue,
    };
  }

  get hasChanged(): boolean {
    if (!this.initialSchedulingSettings) return false;
    return JSON.stringify(this.schedulingSettings) !== JSON.stringify(this.initialSchedulingSettings);
  }

  async mounted() {
    this.setToolbarTitle(this.$t("settings.timeTracking.workingHours").toString());
    const schedulingSettings = await loadSchedulingSettings();
    SchedulingModule.setSchedulingSettings(schedulingSettings);

    this.schedulingSettings = { ...schedulingSettings };
    this.initialSchedulingSettings = JSON.parse(JSON.stringify(schedulingSettings));
  }

  async doSave() {
    const newSettings: SchedulingSettings = {
      ...this.schedulingSettings,
    };

    await saveSchedulingSettings(newSettings);

    SchedulingModule.setSchedulingSettings(newSettings);
    SchedulingModule.updateOccupiedTimeDataForScheduledTasks();
  }
}
